import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../helpers/apiUtils';

/**
 * Auswertung des Welcome-Einladungs-Experiments (Neutaktung 29.09., Plan
 * .claude/plans/2026-09-29-welcome-einladung-neu-takten.md): Wer sich zwischen 00:00 und 16:00 einschreibt, bekommt
 * die erste Einladung entweder sofort (Nacht: 07:00) oder um 16:00 desselben Tags. Gezaehlt wird je Einschreibung
 * (Intention-to-treat), nicht je Anmelder — die Varianten veraendern, wer sich anmeldet.
 */

const API = '/api/webinar-rerun-admin';

interface ArmRow {
  arm: 'sofort' | 'nachmittag';
  eingeschrieben: number;
  nacht: number;
  offen: number;
  mail1: number;
  mail2: number;
  angemeldet: number;
  teilgenommen: number;
  abgemeldet: number;
  reif: number;
  reif_angemeldet: number;
  reif_teilgenommen: number;
}

interface Auswertung {
  version: string | null;
  seit: string | null;
  arme: ArmRow[];
}

const ARM_LABEL: Record<ArmRow['arm'], string> = {
  sofort: 'Sofort (nachts: 07:00)',
  nachmittag: '16:00 am selben Tag',
};

function pct(z: number, n: number): string {
  if (!n) return '–';
  return `${((100 * z) / n).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
}

const TH = { backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' } as const;

interface Quote {
  n: number;
  pro1000: number | null;
  von: number | null;
  bis: number | null;
}

interface BetreffArm {
  variante: 'a' | 'b';
  betreff: string | null;
  empfaenger: number;
  reif: number;
  offen: number;
  geoeffnet: Quote;
  geklickt: Quote;
  angemeldet: Quote;
  teilgenommen: Quote;
}

interface BetreffVersuch {
  versuch: string;
  alt: boolean;
  quelle: string;
  copy_ids: string;
  von: string;
  bis: string;
  arme: BetreffArm[];
}

/** Quote mit 95-%-Intervall in Prozent: „12,5 % (3/24) · 4–31 %". */
function quote(q: Quote, reif: number): React.ReactNode {
  if (!reif || q.pro1000 === null) return '–';
  const p = (v: number | null) => (v === null ? '–' : (v / 10).toLocaleString('de-DE', { maximumFractionDigits: 1 }));
  return (
    <>
      {p(q.pro1000)} % <span style={{ color: '#888' }}>({q.n}/{reif}) · {p(q.von)}–{p(q.bis)} %</span>
    </>
  );
}

/**
 * Betreff-Test der Einladung Mail 1 (P2): Betreff A gegen B je Versuch (Quelle + Fassung des Texts). Gezaehlt wird je
 * Person mit verschickter Mail 1; Raten nur ueber Personen, deren Versand mindestens 72 h her ist.
 */
function BetreffTest(): React.ReactElement {
  const [daten, setDaten] = useState<{ reif_stunden: number; versuche: BetreffVersuch[] } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    const r = await fetchData(`${API}/invite-betreff`);
    if (r?.success) setDaten(r.data as { reif_stunden: number; versuche: BetreffVersuch[] });
    else setFehler(r?.error || 'Betreff-Auswertung konnte nicht geladen werden');
    setLaedt(false);
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  return (
    <Paper sx={{ p: 2, mt: 3, backgroundColor: '#ffffff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ color: 'black' }}>
          Test: Betreff der ersten Einladung (A/B)
        </Typography>
        <Button size="small" startIcon={laedt ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={() => void laden()}>
          Aktualisieren
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
        Mail 1 geht je Empfänger fest mit Betreff A oder B raus. Ein Versuch ist eine Fassung des Texts: Wird der Text
        bearbeitet oder neu erzeugt, beginnt ein neuer Versuch, damit keine zwei Vergleiche gemischt werden. Gezählt wird
        je Person; Öffnungen und Klicks nur auf diese Mail, Anmeldungen nur nach dem Versand für eine Sendung in den
        folgenden 72 Stunden. Quoten nur über Empfänger, deren Versand mindestens 72 Stunden her ist („reif“).
        Öffnungen sind wegen Apple Mail Privacy Protection nur grob aussagekräftig.
      </Typography>
      {fehler && <Alert severity="error">{fehler}</Alert>}
      {daten && daten.versuche.length === 0 && <Alert severity="info">Noch keine Mail 1 mit zwei Betreffen verschickt.</Alert>}
      {daten?.versuche.map((v) => (
        <Box key={`${v.quelle}|${v.versuch}`} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'black' }}>
            {v.quelle} · Fassung {v.alt ? `Text ${v.copy_ids} (vor der Fassungs-Kennung)` : v.versuch} · Versand {v.von.slice(0, 16)} bis{' '}
            {v.bis.slice(0, 16)}
          </Typography>
          <TableContainer sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Betreff', 'Empfänger', 'reif', 'offen', 'Öffnung', 'Klick', 'Webinar-Anmeldung', 'Teilnahme'].map((h) => (
                    <TableCell key={h} sx={TH}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {v.arme.map((a) => (
                  <TableRow key={a.variante}>
                    <TableCell>
                      <strong>{a.variante.toUpperCase()}</strong> {a.betreff ? `„${a.betreff}“` : ''}
                    </TableCell>
                    <TableCell>{a.empfaenger}</TableCell>
                    <TableCell>{a.reif}</TableCell>
                    <TableCell>{a.offen}</TableCell>
                    <TableCell>{quote(a.geoeffnet, a.reif)}</TableCell>
                    <TableCell>{quote(a.geklickt, a.reif)}</TableCell>
                    <TableCell>{quote(a.angemeldet, a.reif)}</TableCell>
                    <TableCell>{quote(a.teilgenommen, a.reif)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Paper>
  );
}

export default function RerunInviteTestSection(): React.ReactElement {
  return (
    <>
      <ZeitpunktTest />
      <BetreffTest />
    </>
  );
}

function ZeitpunktTest(): React.ReactElement {
  const [data, setData] = useState<Auswertung | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await fetchData(`${API}/invite-test`);
    if (r?.success) setData(r.data as Auswertung);
    else setError(r?.error || 'Auswertung konnte nicht geladen werden');
    setLoading(false);
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  return (
    <Paper sx={{ p: 2, mt: 3, backgroundColor: '#ffffff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ color: 'black' }}>
          Test: Zeitpunkt der ersten Einladung
        </Typography>
        <Button size="small" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={() => void laden()}>
          Aktualisieren
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
        Wer sich bis 16 Uhr neu anmeldet, bekommt die erste Einladung entweder sofort (bei Anmeldung in der Nacht um
        7 Uhr) oder um 16 Uhr desselben Tages. Die Gruppe wird per Zufall fest zugeteilt. Gezählt wird je Anmeldung,
        auch wenn gar keine Mail rausging. Die Quoten beziehen sich nur auf Anmeldungen, deren Sendungen schon gelaufen
        sind (älter als ~3 Tage). Kleine Unterschiede sind bei dieser Menge Zufall – erst nach einigen Wochen belastbar.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {data && !data.arme.some((a) => a.eingeschrieben > 0) && (
        <Alert severity="info">Noch keine Anmeldungen im Test{data.version ? ` (Version ${data.version})` : ''}.</Alert>
      )}
      {data && data.arme.some((a) => a.eingeschrieben > 0) && (
        <TableContainer sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'Gruppe',
                  'Anmeldungen',
                  'davon nachts',
                  '1. Einladung',
                  '2. Einladung',
                  'Webinar-Anmeldung',
                  'Teilnahme',
                  'Teilnahme je Anmeldung*',
                  'Webinar-Anmeldung je Anmeldung*',
                  'abgemeldet',
                ].map((h) => (
                  <TableCell key={h} sx={TH}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.arme.map((a) => (
                <TableRow key={a.arm}>
                  <TableCell>{ARM_LABEL[a.arm]}</TableCell>
                  <TableCell>{a.eingeschrieben}</TableCell>
                  <TableCell>{a.nacht}</TableCell>
                  <TableCell>{a.mail1}</TableCell>
                  <TableCell>{a.mail2}</TableCell>
                  <TableCell>{a.angemeldet}</TableCell>
                  <TableCell>{a.teilgenommen}</TableCell>
                  <TableCell>
                    {pct(a.reif_teilgenommen, a.reif)} <span style={{ color: '#888' }}>({a.reif_teilgenommen}/{a.reif})</span>
                  </TableCell>
                  <TableCell>
                    {pct(a.reif_angemeldet, a.reif)} <span style={{ color: '#888' }}>({a.reif_angemeldet}/{a.reif})</span>
                  </TableCell>
                  <TableCell>{pct(a.abgemeldet, a.eingeschrieben)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data?.seit && (
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
          * nur Anmeldungen älter als ~3 Tage · Test {data.version} seit {data.seit.slice(0, 16)} · Teilnahme = Video im
          Raum gestartet
        </Typography>
      )}
    </Paper>
  );
}
